import { useEffect, useMemo, useState } from 'react'
import { Box, Fab, LinearProgress, Alert, Stack, Tabs, Tab } from '@mui/material'
import { Add, Undo as UndoIcon } from '@mui/icons-material'
import _ from 'lodash'
import { useFarmId } from '~/common/use-farm-id'
import { useOrgParams } from '~/common/use-org-params'
import { useOrganizations } from '~/common/use-organizations'
import { setActiveOrgs, useOrgContextDispatch } from '~/common/context/organization/org-context'
import { OrgBreadcrumbs } from '~/dashboard/org-breadcrumbs'
import {
  APPBAR_HEIGHT_REM,
  APPBAR_EXTRA_DESKTOP_REM,
  CONTENT_PADDING_RBL_REM,
  CONTENT_PADDING_T_REM,
  DASHBOARD_TITLE_HEIGHT_REM,
  FULLSCREEN_HEIGHT_MD,
} from '~/common/constants/layout-constants'
import { MapCard } from './map-card'
import { MapRefreshProvider } from './context/map-refresh-context'
import { setSelectedCrop, useMapInspectorContext, useMapInspectorContextDispatch } from './context/map-inspector-context'
import { STATUS_COLORS_SELECTED } from './map-constants'
import { MapInspectorPanel } from './map-inspector/map-inspector-panel'
import { MapInspectorProvider } from './context/map-inspector-context-provider'
import { Edit, MultipleStop } from '@mui/icons-material'
import { usePlotPolygons } from './use-plot-polygons'
import { usePolygonEdit } from '~/plot/edit-plot/use-polygon-edit'
import { PolygonEditLayer } from '~/plot/edit-plot/polygon-edit-layer'
import { MapPlotEditorPanel } from './map-plot-editor-panel'
import { AddPlotModal } from '~/plot/add-plot/add-plot-modal'
import { useFeatures } from '~/common/use-features'

// Max height of the map on mobile — 33vh minus same elements as containerHeight
const MAP_MOBILE_MAX_HEIGHT_MD = `calc(33vh - ${APPBAR_HEIGHT_REM})`
const MAP_MOBILE_MAX_HEIGHT_XS = `calc(33vh - ${APPBAR_HEIGHT_REM} + ${APPBAR_EXTRA_DESKTOP_REM})`

// Inner component that has access to MapInspectorContextDispatch
const MapFullscreenInner = ({ farmId }) => {
  const dispatch = useOrgContextDispatch()
  const inspectorDispatch = useMapInspectorContextDispatch()
  const { selectedCropOrgId, plotStatusMap } = useMapInspectorContext()
  const isFarmSelected = selectedCropOrgId === farmId
  const farmTier = isFarmSelected ? (plotStatusMap?.get(farmId) ?? 'neutral') : null
  const farmBorderColor = farmTier ? (STATUS_COLORS_SELECTED[farmTier] ?? STATUS_COLORS_SELECTED.neutral) : null
  const { plotId } = useOrgParams()
  const [inspectionTab, setInspectionTab] = useState(0)
  const [addPlotOpen, setAddPlotOpen] = useState(false)
  const { features } = useFeatures()

  const { plots } = usePlotPolygons(farmId)
  const selectedPlot = plots.find(p => p.id === plotId)
  const { currentPoints, isDirty, canUndo, updateVertex, translateAll, insertVertex, undo, reset } = usePolygonEdit({ originalPoints: selectedPlot?.points })

  // Reset edit state when the selected plot changes
  useEffect(() => {
    reset()
  }, [plotId]) // eslint-disable-line react-hooks/exhaustive-deps

  const ids = useMemo(() => _.compact([farmId, plotId]), [farmId, plotId])
  const typeValidations = useMemo(
    () => (plotId ? ['farm', 'plot'] : ['farm']),
    [plotId]
  )

  const { data: organizations } = useOrganizations({ ids, typeValidations })

  const orderedOrgs = useMemo(() => {
    if (!organizations) return []
    return ids.map(id => organizations.find(org => org.id === id)).filter(Boolean)
  }, [organizations, ids])

  useEffect(() => {
    dispatch(setActiveOrgs({ activeOrgs: orderedOrgs }))
  }, [dispatch, orderedOrgs])

  // Clear selected crop when farm or plot changes (prevents ghost selections)
  useEffect(() => {
    inspectorDispatch(setSelectedCrop({ cropId: null }))
  }, [farmId, plotId, inspectorDispatch])

  const handleChange = (event, newValue) => {
    setInspectionTab(newValue)
  }

  return (
    <Stack
      direction={{ xs: 'column', lg: 'row' }}
      sx={{
        height: {
          xs: `calc(100vh - ${APPBAR_HEIGHT_REM} + ${APPBAR_EXTRA_DESKTOP_REM})`,
          md: `calc(100vh - ${APPBAR_HEIGHT_REM})`,
          lg: FULLSCREEN_HEIGHT_MD,
        },
        m: `-${CONTENT_PADDING_T_REM} -${CONTENT_PADDING_RBL_REM} -${CONTENT_PADDING_RBL_REM} -${CONTENT_PADDING_RBL_REM}`,
        display: 'flex',
        alignItems: 'stretch',
        gap: { xs: 1, lg: 0 },
      }}
    >
      {/* Map column — full width on mobile, 50% on desktop */}
      <Box
        sx={{
          width: { xs: '100%', lg: '50%' },
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          px: CONTENT_PADDING_RBL_REM,
          pt: CONTENT_PADDING_T_REM,
          pb: { xs: 0, lg: CONTENT_PADDING_RBL_REM },
        }}
      >
        {/* Breadcrumbs header */}
        <Box
          sx={{
            display: 'flex',
            height: DASHBOARD_TITLE_HEIGHT_REM,
            mb: '0.25rem',
            pt: 1,
            flexShrink: 0,
          }}
        >
          <OrgBreadcrumbs baseRoute='map' />
        </Box>

        {/* Map — explicit height on mobile, fills remaining space on desktop */}
        <Box
          sx={{
            flex: { lg: 1 },
            height: { xs: MAP_MOBILE_MAX_HEIGHT_XS, md: MAP_MOBILE_MAX_HEIGHT_MD, lg: 'unset' },
            overflow: 'hidden',
            position: 'relative',
            transition: 'box-shadow 200ms ease-in-out',
            mx: { xs: `-${CONTENT_PADDING_RBL_REM}`, lg: 0 },
          }}
        >
          <MapCard
            fullscreen
            orgZoom={true}
            showRelationshipLegend={inspectionTab === 1}
            editingPlot={inspectionTab === 0 && isDirty && plotId ? { id: plotId, points: currentPoints } : null}
            editLayer={
              inspectionTab === 0 && plotId && selectedPlot
                ? <PolygonEditLayer points={currentPoints} updateVertex={updateVertex} translateAll={translateAll} insertVertex={insertVertex} />
                : null
            }
          />
          {inspectionTab === 0 && canUndo && (
            <Fab size="small" onClick={undo} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1000 }}>
              <UndoIcon fontSize="small" />
            </Fab>
          )}
          <style>
            {`
              .tp-map-border-img.leaflet-image-layer {
                border-radius: 6px;
              }
            `}
          </style>
          {farmBorderColor && (
            <style>
              {`
                .tp-map-border-img.leaflet-image-layer {
                  outline: 4px solid ${farmBorderColor};
                  outline-offset: -4px;
                }
              `}
            </style>
          )}
        </Box>
      </Box>

      {/* Inspector panel — fills remaining space */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          borderLeft: { xs: 'none', lg: '1px solid' },
          borderTop: { xs: '1px solid', lg: 'none' },
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          minHeight: 0,

          '& .MuiTabs-list': {
            minHeight: 7,
          },

          '& .MuiTab-root': {
            minHeight: 7,
          },
        }}
      >
        <Tabs value={inspectionTab} onChange={handleChange}>
          {features['edit_plots'] && (
            <Tab icon={<Edit />} iconPosition='start' label='Plot Editor' />
          )}
          {features['crop_relationships'] && (
            <Tab icon={<MultipleStop />} iconPosition='start' label='Crop Relationships' />
          )}
        </Tabs>

        {features['edit_plots'] && inspectionTab === 0 && (
          <MapPlotEditorPanel
            plotId={plotId}
            plots={plots}
            isDirty={isDirty}
            currentPoints={currentPoints}
            onDiscard={reset}
          />
        )}
        {features['crop_relationships'] && inspectionTab === 1 && (
          <MapInspectorPanel />
        )}
      </Box>
      
      {features['add_plots'] && (
        <Fab
          size="small"
          color="primary"
          onClick={() => setAddPlotOpen(true)}
          sx={{
            position: 'absolute',
            bottom: 28,
            right: 16,
          }}
        >
          <Add />
        </Fab>
      )}

      <AddPlotModal open={addPlotOpen} onClose={() => setAddPlotOpen(false)} />
    </Stack>
  )
}

function MapFullscreen() {
  const { farmId, loading } = useFarmId()

  if (loading) {
    return <LinearProgress />
  }

  if (!farmId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a farm to view the map.</Alert>
      </Box>
    )
  }

  return (
    <MapRefreshProvider>
      <MapInspectorProvider>
        <MapFullscreenInner farmId={farmId} />
      </MapInspectorProvider>
    </MapRefreshProvider>
  )
}

export default MapFullscreen
